import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Home", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
  { to: "/plans", label: "Plans", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" },
  { to: "/inspections", label: "Inspections", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" },
  { to: "/templates", label: "Templates", icon: "M4 6h16M4 10h16M4 14h16M4 18h16" },
  { to: "/vessels", label: "Vessels", icon: "M3 17l3-10h12l3 10M3 17h18M7 17v2a1 1 0 001 1h8a1 1 0 001-1v-2" },
  { to: "/companies", label: "Companies", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
];

function NavIcon({ path }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
      strokeWidth={1.5} stroke="currentColor" style={{ width: 20, height: 20, flexShrink: 0 }}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

function OskarLogo({ size = "md", isMobile = false }) {
  const large = size === "lg";
  return (
    <NavLink to="/dashboard" style={{ display: "block", lineHeight: 0 }}>
    <img
      src="/oskar_india_tango_logo.png"
      alt="Oskar India Tango"
      style={{
        height: large ? 68 : (isMobile ? 36 : 58),
        width: "auto",
        display: "block",
        userSelect: "none",
      }}
    />
    </NavLink>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function TrialBanner() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    if (!token) return;
    const API = process.env.REACT_APP_API_URL || "";
    fetch(`${API}/billing/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBillingStatus(data); })
      .catch(() => {});
  }, [token]);

  if (!billingStatus) return null;
  const { subscription_status, trial_days_remaining } = billingStatus;
  if (subscription_status === "active") return null;

  const isExpired = subscription_status === "expired";
  const daysLeft  = trial_days_remaining ?? 0;
  const urgent    = isExpired || daysLeft <= 3;

  return (
    <div style={{
      background: urgent ? "#fdecea" : "#fff8e1",
      borderBottom: `1px solid ${urgent ? "#f5c6c2" : "#ffe082"}`,
      padding: "8px 28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: 13,
      color: urgent ? "#c0392b" : "#7a5c00",
      flexShrink: 0,
    }}>
      <span>
        {isExpired
          ? "Your trial has expired. Subscribe to continue using Oskar India Tango."
          : daysLeft === 0
          ? "Your trial expires today."
          : `Your free trial ends in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}.`}
      </span>
      <button
        onClick={() => navigate("/billing")}
        style={{
          background: urgent ? "#c0392b" : "#1e3a5f",
          color: "white",
          border: "none",
          borderRadius: 6,
          padding: "5px 14px",
          fontSize: 12,
          fontWeight: 500,
          cursor: "pointer",
          whiteSpace: "nowrap",
          marginLeft: 16,
        }}
      >
        {isExpired ? "Subscribe now" : "View plans"}
      </button>
    </div>
  );
}

export default function AppShell() {
  const { user, activeCompany, logout } = useAuth();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const profileRef = useRef(null);

  const initials = user
    ? ((user.first_name?.[0] || "") + (user.last_name?.[0] || "")).toUpperCase() || user.email?.[0]?.toUpperCase() || "U"
    : "U";

  useEffect(() => {
    function handleClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const sidebarBg = "#1e3a5f";
  const sidebarHover = "rgba(255,255,255,0.08)";
  const sidebarActive = "rgba(255,255,255,0.14)";
  const accentColor = "#63b3ed";

  return (
    <div style={{ minHeight: "100vh", background: "#e2eaf4", display: "flex", flexDirection: "column" }}>

      {/* Top bar */}
      <div style={{
        height: isMobile ? 56 : 80,
        background: sidebarBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 28px",
        flexShrink: 0,
        zIndex: 30,
        position: "relative",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}>
        {/* Left: hamburger + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => setSidebarOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "white", display: "flex", alignItems: "center" }}
            aria-label="Open menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: 22, height: 22 }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <OskarLogo size="sm" isMobile={isMobile} />
        </div>

        {/* Right: company + avatar */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {activeCompany?.name && !isMobile && (
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", letterSpacing: "0.03em", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {activeCompany.name}
            </span>
          )}
          <div ref={profileRef} style={{ position: "relative" }}>
            <button
              onClick={() => setProfileOpen(p => !p)}
              style={{
                width: 32, height: 32, borderRadius: "50%",
                background: accentColor,
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 500, color: "#0c2a4a",
              }}
            >
              {initials}
            </button>
            {profileOpen && (
              <div style={{
                position: "absolute", right: 0, top: 40,
                background: "white", border: "1px solid #d0dcea",
                borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                width: 160, overflow: "hidden", zIndex: 100,
              }}>
                <div style={{ padding: "10px 14px 8px", borderBottom: "1px solid #e8eef5" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#1e3a5f" }}>{user?.first_name} {user?.last_name}</div>
                  <div style={{ fontSize: 11, color: "#6b8cae", marginTop: 1 }}>{user?.email}</div>
                </div>
                <button onClick={() => { navigate("/profile"); setProfileOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#1e3a5f", background: "none", border: "none", cursor: "pointer" }}
                  onMouseEnter={e => e.target.style.background = "#f0f4f8"}
                  onMouseLeave={e => e.target.style.background = "none"}>
                  Profile
                </button>
                <button onClick={() => { navigate("/billing"); setProfileOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#1e3a5f", background: "none", border: "none", cursor: "pointer" }}
                  onMouseEnter={e => e.target.style.background = "#f0f4f8"}
                  onMouseLeave={e => e.target.style.background = "none"}>
                  Billing
                </button>
                <button onClick={() => { navigate("/plans"); setProfileOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#1e3a5f", background: "none", border: "none", cursor: "pointer" }}
                  onMouseEnter={e => e.target.style.background = "#f0f4f8"}
                  onMouseLeave={e => e.target.style.background = "none"}>
                  Plans
                </button>
                <button onClick={() => { logout(); setProfileOpen(false); }}
                  style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", fontSize: 13, color: "#c0392b", background: "none", border: "none", cursor: "pointer", borderTop: "1px solid #e8eef5" }}
                  onMouseEnter={e => e.target.style.background = "#fff5f5"}
                  onMouseLeave={e => e.target.style.background = "none"}>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Trial banner */}
      <TrialBanner />

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 40 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: "absolute", left: 0, top: 0, bottom: 0,
              width: 240, background: sidebarBg,
              display: "flex", flexDirection: "column",
              boxShadow: "4px 0 24px rgba(0,0,0,0.2)",
            }}
          >
            {/* Sidebar header */}
            <div style={{ padding: "24px 20px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <OskarLogo size="lg" />
              {activeCompany?.name && (
                <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  {activeCompany.name}
                </div>
              )}
            </div>

            {/* Nav items */}
            <nav style={{ flex: 1, padding: "12px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
              {NAV_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setSidebarOpen(false)}
                  style={({ isActive }) => ({
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 12px", borderRadius: 8,
                    textDecoration: "none",
                    fontSize: 14, fontWeight: isActive ? 500 : 400,
                    color: isActive ? "white" : "rgba(255,255,255,0.65)",
                    background: isActive ? sidebarActive : "transparent",
                    transition: "background 0.15s, color 0.15s",
                  })}
                  onMouseEnter={e => { if (!e.currentTarget.dataset.active) e.currentTarget.style.background = sidebarHover; }}
                  onMouseLeave={e => { if (!e.currentTarget.dataset.active) e.currentTarget.style.background = "transparent"; }}
                >
                  <NavIcon path={item.icon} />
                  {item.label}
                </NavLink>
              ))}

              {user?.role === "admin" && (
                <NavLink
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  style={({ isActive }) => ({
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 12px", borderRadius: 8,
                    textDecoration: "none",
                    fontSize: 14, fontWeight: isActive ? 500 : 400,
                    color: isActive ? "white" : "rgba(255,255,255,0.65)",
                    background: isActive ? sidebarActive : "transparent",
                    marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16,
                  })}
                >
                  <NavIcon path="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  Admin
                </NavLink>
              )}
            </nav>

            {/* Sidebar footer */}
            <div style={{ padding: "14px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: 11, color: "rgba(255,255,255,0.25)" }}>
              oskar-india-tango.app
              <div style={{ marginTop: 6, display: "flex", gap: 10 }}>
                <a href="/privacy" style={{ color: "rgba(255,255,255,0.25)", textDecoration: "none" }}>Privacy</a>
                <span>·</span>
                <a href="/terms" style={{ color: "rgba(255,255,255,0.25)", textDecoration: "none" }}>Terms</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page content */}
      <div style={{ flex: 1, background: "#e2eaf4", minHeight: "100%" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "16px 16px" : "32px 40px", boxSizing: "border-box" }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}